/** @format */
/* eslint-disable @typescript-eslint/naming-convention */

import { Component, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID } from '@angular/core';
import {
	IMAGE_CONFIG,
	IMAGE_LOADER,
	ImageConfig,
	ImageLoaderConfig,
	isPlatformBrowser
} from '@angular/common';

import { Scene7ImageLoaderService } from '@shared/media/data-access';
import { BreakPointMinWidthValues, LoadingStrategy } from '@shared/data-access/enums';
import { IsBotService, WINDOW } from '@shared/data-access/services';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ImageOptimizedSize } from './image-optimized-size.enum';

@Component({
	selector: 'shared-media-component-optimized-image',
	templateUrl: './optimized-image.component.html',
	styleUrl: './optimized-image.component.scss',
	providers: [
		{
			provide: IMAGE_LOADER,
			useFactory:
				(imageLoaderService: Scene7ImageLoaderService) => (config: ImageLoaderConfig) =>
					imageLoaderService.buildUrlByImageLoader(config),
			deps: [Scene7ImageLoaderService]
		},
		{
			provide: IMAGE_CONFIG,
			useValue: {
				// Custom breakpoints based on T-shirt Size Breakpoints
				// https://homefield.dcsg.com/?path=/docs/resources-styles-breakpoints--docs
				breakpoints: [
					ImageOptimizedSize.THUMBNAIL_XS,
					ImageOptimizedSize.THUMBNAIL_M,
					BreakPointMinWidthValues.EXTRASMALL,
					BreakPointMinWidthValues.SMALL,
					ImageOptimizedSize.TABLET_XS,
					BreakPointMinWidthValues.MEDIUM,
					BreakPointMinWidthValues.LARGE,
					BreakPointMinWidthValues.EXTRALARGE,
					BreakPointMinWidthValues.EXTRAEXTRALARGE
				]
			}
		}
	],
	standalone: false
})
export class OptimizedImageComponent implements OnInit {
	@Input({ required: true }) imageLoaderConfig: ImageLoaderConfig = {
		src: 'ImageUnavailablePDPSwatch', // eCode or imageUrl
		width: 16,
		loaderParams: {
			sizes: '(max-width: 320px) 100vw, 50vw',
			swatchColor: '',
			swatchColorPrefix: '',
			isUrlEmbeded: false //This value is true only if the src is the complete image URL
		}
	};
	@Input({ required: true }) altText = '';
	@Input() isPriority = false;
	@Input() loadingStrategy = LoadingStrategy.Lazy;
	imageUrl: string;

	@Output() handleError: EventEmitter<string> = new EventEmitter();
	@Output() public optimizedImageLoaded: EventEmitter<any> = new EventEmitter<any>();

	constructor(
		@Inject(IMAGE_CONFIG) public imageConfig: ImageConfig,
		@Inject(IMAGE_LOADER) private imageLoader: (config: ImageLoaderConfig) => string,
		@Inject(PLATFORM_ID) protected platformId: object,
		@Inject(WINDOW) protected window: Window,
		private analyticsService: ProductAnalyticsService,
		private isBotService: IsBotService
	) {}

	ngOnInit(): void {
		this.imageUrl = this.imageLoader(this.imageLoaderConfig);
	}

	/**
	 * @description Posts to rumrunner when a missing product image is detected
	 */
	handleImageError(requestedImg: string): void {
		if (
			isPlatformBrowser(this.platformId) &&
			!this.isBotService.isBot(window.navigator.userAgent)
		) {
			const ecode = this.imageLoaderConfig.src;
			const color = this.imageLoaderConfig.loaderParams?.swatchColor?.slice(1);

			const eventPayload: any = {
				pageUrl: window.location.href,
				requestedImg,
				productName: this.imageLoaderConfig.src
			};

			if (ecode) {
				eventPayload.ecode = ecode;
			}

			if (color) {
				eventPayload.color = color;
			}

			this.handleError.emit(requestedImg);

			this.analyticsService.emitMissingImageEvent(eventPayload);
		}
	}

	getLoadingStrategy(): string {
		return this.isPriority ? LoadingStrategy.Eager : this.loadingStrategy;
	}
}
